import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Settings as SettingsIcon,
    Database,
    Download,
    Upload,
    Server,
    Mail,
    Shield,
    Save,
    RefreshCw,
    AlertTriangle,
    CheckCircle,
    Info,
    Image,
    X,
    Code,
    Eye,
    EyeOff,
    GitBranch,
    RotateCcw,
    Clock,
    Key,
    MessageSquare,
    Plug,
    Phone,
    FileText,
    Search,
    ChevronDown
} from 'lucide-react';
import toast from 'react-hot-toast';

import { apiService } from '../services/api';
import { applyBrandingColors } from '../utils/branding';
import LicenseManagerPanel from '../components/LicenseManagerPanel';
import NotificationTemplates from './NotificationTemplates';
import ProtocolSettingsManager from '../components/ProtocolSettingsManager';
import { PageLayout, PageSection, Card, CardHeader, CardTitle, CardContent, Button } from '../components/ui';

const ENVIRONMENT_CATEGORY_METADATA = {
    'Database Configuration': {
        icon: Database,
        titleKey: 'settings.environment.categories.database.title',
        descriptionKey: 'settings.environment.categories.database.description',
        defaultTitle: 'Database Configuration',
        defaultDescription: 'PostgreSQL connection settings'
    },
    'Database Pool Settings': {
        icon: Database,
        titleKey: 'settings.environment.categories.pool.title',
        descriptionKey: 'settings.environment.categories.pool.description',
        defaultTitle: 'Database Pool Settings',
        defaultDescription: 'Database connection pooling options'
    },
    'Server Configuration': {
        icon: Server,
        titleKey: 'settings.environment.categories.server.title',
        descriptionKey: 'settings.environment.categories.server.description',
        defaultTitle: 'Server Configuration',
        defaultDescription: 'Application server settings'
    },
    'JWT Configuration': {
        icon: Shield,
        titleKey: 'settings.environment.categories.jwt.title',
        descriptionKey: 'settings.environment.categories.jwt.description',
        defaultTitle: 'JWT Configuration',
        defaultDescription: 'JSON Web Token authentication settings'
    },
    'Redis Configuration': {
        icon: Database,
        titleKey: 'settings.environment.categories.redis.title',
        descriptionKey: 'settings.environment.categories.redis.description',
        defaultTitle: 'Redis Configuration',
        defaultDescription: 'Redis cache server settings'
    },
    'Logging Configuration': {
        icon: FileText,
        titleKey: 'settings.environment.categories.logging.title',
        descriptionKey: 'settings.environment.categories.logging.description',
        defaultTitle: 'Logging Configuration',
        defaultDescription: 'Application logging settings'
    },
    'Email Configuration': {
        icon: Mail,
        titleKey: 'settings.environment.categories.email.title',
        descriptionKey: 'settings.environment.categories.email.description',
        defaultTitle: 'Email Configuration',
        defaultDescription: 'SMTP settings for sending email notifications'
    },
    'Twilio Configuration': {
        icon: Phone,
        titleKey: 'settings.environment.categories.twilio.title',
        descriptionKey: 'settings.environment.categories.twilio.description',
        defaultTitle: 'Twilio Configuration',
        defaultDescription: 'SMS and WhatsApp messaging via Twilio'
    },
    'Security Configuration': {
        icon: Shield,
        titleKey: 'settings.environment.categories.security.title',
        descriptionKey: 'settings.environment.categories.security.description',
        defaultTitle: 'Security Configuration',
        defaultDescription: 'Security and rate limiting settings'
    },
    'File Upload Configuration': {
        icon: Upload,
        titleKey: 'settings.environment.categories.fileUpload.title',
        descriptionKey: 'settings.environment.categories.fileUpload.description',
        defaultTitle: 'File Upload Configuration',
        defaultDescription: 'File upload size and storage settings'
    },
    'OTA Configuration': {
        icon: RefreshCw,
        titleKey: 'settings.environment.categories.ota.title',
        descriptionKey: 'settings.environment.categories.ota.description',
        defaultTitle: 'OTA Configuration',
        defaultDescription: 'Over-the-air firmware update settings'
    },
    'WebSocket Configuration': {
        icon: Plug,
        titleKey: 'settings.environment.categories.websocket.title',
        descriptionKey: 'settings.environment.categories.websocket.description',
        defaultTitle: 'WebSocket Configuration',
        defaultDescription: 'Real-time connection settings'
    }
};

const SMTP_VARIABLE_KEYS = ['SMTP_HOST', 'SMTP_PORT', 'SMTP_SECURE', 'SMTP_USER', 'SMTP_PASSWORD'];

const SMTP_FIELD_GROUPS = [
    {
        id: 'connection',
        titleKey: 'settings.email.groups.connection.title',
        descriptionKey: 'settings.email.groups.connection.description',
        defaultTitle: 'SMTP Connection',
        defaultDescription: 'Server and port used to send email notifications',
        fields: [
            {
                key: 'SMTP_HOST',
                type: 'text',
                labelKey: 'settings.email.smtpHost',
                placeholderKey: 'settings.email.smtpHostPlaceholder',
                defaultLabel: 'SMTP Host',
                defaultPlaceholder: 'smtp.gmail.com'
            },
            {
                key: 'SMTP_PORT',
                type: 'number',
                labelKey: 'settings.email.smtpPort',
                placeholderKey: 'settings.email.smtpPortPlaceholder',
                defaultLabel: 'SMTP Port',
                defaultPlaceholder: '587'
            },
            {
                key: 'SMTP_SECURE',
                type: 'select',
                labelKey: 'settings.email.smtpSecure',
                defaultLabel: 'Encryption',
                helperKey: 'settings.email.smtpSecureHelper',
                defaultHelper: 'Choose encryption method based on your SMTP server requirements',
                options: [
                    {
                        value: 'false',
                        labelKey: 'settings.environment.smtp.starttls',
                        defaultLabel: 'STARTTLS (Port 587) - Recommended'
                    },
                    {
                        value: 'true',
                        labelKey: 'settings.environment.smtp.ssl',
                        defaultLabel: 'SSL/TLS (Port 465)'
                    },
                    {
                        value: 'none',
                        labelKey: 'settings.environment.smtp.none',
                        defaultLabel: 'None (Port 25) - Not Recommended'
                    }
                ]
            }
        ]
    },
    {
        id: 'credentials',
        titleKey: 'settings.email.groups.credentials.title',
        descriptionKey: 'settings.email.groups.credentials.description',
        defaultTitle: 'Credentials',
        defaultDescription: 'Account used to authenticate with the SMTP server',
        fields: [
            {
                key: 'SMTP_USER',
                type: 'text',
                labelKey: 'settings.email.username',
                defaultLabel: 'Username'
            },
            {
                key: 'SMTP_PASSWORD',
                type: 'password',
                labelKey: 'settings.email.password',
                defaultLabel: 'Password',
                sensitive: true
            }
        ]
    }
];

const TAB_IDS = ['system', 'license', 'platform', 'branding', 'notifications', 'protocol', 'environment', 'database', 'email', 'security'];

// Common typos mapping
const TAB_ALIASES = {
    'enviroment': 'environment',
    'environement': 'environment',
    'envrionment': 'environment',
    'licence': 'license',
    'lisence': 'license',
    'notifcations': 'notifications',
    'notificatons': 'notifications',
    'protocal': 'protocol',
    'datbase': 'database',
    'databse': 'database',
    'secuirty': 'security',
    'sercurity': 'security'
};

const getTabFromSearch = (search) => {
    const params = new URLSearchParams(search);
    let tabParam = params.get('tab');

    // Handle null/empty
    if (!tabParam) {
        return 'system';
    }

    // Normalize to lowercase for comparison
    const normalizedTab = tabParam.toLowerCase();

    // Check if it's a valid tab
    if (TAB_IDS.includes(normalizedTab)) {
        return normalizedTab;
    }

    // Check if it's a known typo/alias
    if (TAB_ALIASES[normalizedTab]) {
        return TAB_ALIASES[normalizedTab];
    }

    // Default to system tab
    return 'system';
};

function Settings() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const updateCopy = useMemo(() => {
        const result = t('settings.update', { returnObjects: true });
        return typeof result === 'string' ? {} : result;
    }, [t]);

    const getUpdate = (path, fallback) => {
        const value = path.split('.').reduce((acc, key) => {
            if (acc && typeof acc === 'object' && key in acc) {
                return acc[key];
            }
            return undefined;
        }, updateCopy);
        return value ?? fallback;
    };

    const resolveValue = (value, fallbackKey = 'settings.system.unknown') => {
        if (value === undefined || value === null) {
            return t(fallbackKey, 'Unknown');
        }

        if (typeof value === 'string') {
            const trimmed = value.trim().toLowerCase();
            if (!trimmed || trimmed === 'unknown' || trimmed === 'n/a') {
                return t(fallbackKey, 'Unknown');
            }
        }

        return value;
    };

    const [activeTab, setActiveTab] = useState(() => getTabFromSearch(location.search));
    const [backupLoading, setBackupLoading] = useState(false);
    const [envVars, setEnvVars] = useState({});
    const [envValidation, setEnvValidation] = useState({ valid: true, errors: [], warnings: [] });
    const [logoUploading, setLogoUploading] = useState(false);
    const [autoRestartPm2, setAutoRestartPm2] = useState(true); // Auto-restart by default
    const [savingEmailSettings, setSavingEmailSettings] = useState(false);
    const [savingEnvVars, setSavingEnvVars] = useState(false);
    const [isTestingEmail, setIsTestingEmail] = useState(false);
    const [envSearchTerm, setEnvSearchTerm] = useState('');
    const [collapsedCategories, setCollapsedCategories] = useState({});
    const [systemSettings, setSystemSettings] = useState({
        siteName: 'Sensity',
        adminEmail: '',
        backupRetentionDays: 30,
        logRetentionDays: 7,
        alertsEnabled: true,
        maintenanceMode: false
    });
    const [showSensitive, setShowSensitive] = useState({});

    const handleSensitiveToggle = async (varName) => {
        const nextVisible = !showSensitive[varName];
        setShowSensitive(prev => ({ ...prev, [varName]: nextVisible }));

        if (nextVisible && envVars[varName] === '***MASKED***') {
            try {
                const response = await apiService.getEnvironmentVariables([varName]);
                const revealedValue = response?.variables?.[varName];
                if (revealedValue !== undefined) {
                    setEnvVars(prev => ({ ...prev, [varName]: revealedValue }));
                }
            } catch (error) {
                console.error('Failed to reveal sensitive variable:', error);
                setShowSensitive(prev => ({ ...prev, [varName]: false }));
                toast.error(t('settings.environment.revealError', 'Failed to reveal value. Please try again.'));
            }
        }
    };
    const [smtpValues, setSmtpValues] = useState({});
    const [smtpRequiresRestart, setSmtpRequiresRestart] = useState(false);

    const [brandingSettings, setBrandingSettings] = useState({
        companyName: 'Sensity',
        companyLogo: null,
        logoPreview: null,
        favicon: null,
        primaryColor: '#2563eb',
        accentColor: '#1d4ed8'
    });

    // Sync tab selection with query parameter for deep links
    // This effect only runs when the URL changes (not when activeTab changes via clicks)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const rawTabParam = params.get('tab');
        const resolvedTab = getTabFromSearch(location.search);

        // If the resolved tab is different from what's in the URL, update the URL
        // This handles typos and invalid tab names
        if (rawTabParam && rawTabParam !== resolvedTab) {
            params.set('tab', resolvedTab);
            navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
            setActiveTab(resolvedTab);
            return;
        }

        // Update active tab to match URL
        if (resolvedTab !== activeTab) {
            setActiveTab(resolvedTab);
        }
    }, [location.search, location.pathname, navigate]);

    // Update URL when tab changes via user interaction (clicking tabs)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const currentTabInUrl = params.get('tab');

        // Only update URL if the active tab differs from what's in the URL
        // and the activeTab is a valid tab (not from initial state)
        if (activeTab && currentTabInUrl !== activeTab && TAB_IDS.includes(activeTab)) {
            params.set('tab', activeTab);
            navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
        }
    }, [activeTab, location.pathname, navigate]);

    // Query settings
    const { data: settingsData, isLoading: settingsLoading } = useQuery(
        'settings',
        () => apiService.getSettings(),
        {
            refetchOnWindowFocus: false, // Disable auto-refresh
            staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
            onSuccess: (data) => {
                if (data) {
                    setSystemSettings(prev => ({ ...prev, ...data.system }));
                    setBrandingSettings(prev => ({
                        ...prev,
                        ...data.branding,
                        logoPreview: data.branding?.companyLogo ? `/api/settings/logo?${Date.now()}` : null
                    }));
                    // Apply server branding
                    if (data.branding?.primaryColor) {
                        applyBrandingColors(data.branding.primaryColor);
                    }
                }
            },
            onError: () => {
                // Settings endpoint might not exist yet - this is fine
            }
        }
    );

    // Query system info - NO auto-refresh
    const { data: systemInfo, isLoading: systemLoading } = useQuery(
        'system-info',
        () => apiService.getSystemInfo ? apiService.getSystemInfo() : Promise.resolve({}),
        {
            refetchOnWindowFocus: false, // Disable auto-refresh
            onError: () => {
                // System info endpoint might not exist yet
            }
        }
    );

    // Query system health - NO auto-refresh
    const { data: systemHealth, isLoading: healthLoading } = useQuery(
        'system-health',
        () => apiService.getSystemHealth ? apiService.getSystemHealth() : Promise.resolve({}),
        {
            refetchOnWindowFocus: false, // Disable auto-refresh
            onError: () => {
                // System health endpoint might not exist yet
            }
        }
    );

    // Query environment variables
    const { data: envData, isLoading: envLoading, error: envError, refetch: refetchEnv } = useQuery(
        'environment-variables',
        () => apiService.getEnvironmentVariables(),
        {
            refetchOnWindowFocus: false, // Disable auto-refresh
            staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
            onSuccess: (data) => {
                if (data?.variables) {
                    setEnvVars(data.variables);
                }
            },
            onError: (error) => {
                console.error('Failed to fetch environment variables:', error);

                // Don't show error toast if we're currently saving env vars (PM2 might be restarting)
                if (savingEnvVars || savingEmailSettings) {
                    console.log('Suppressing error toast during save operation (server may be restarting)');
                    return;
                }

                // Show detailed error for 403 Forbidden
                if (error.response?.status === 403) {
                    const details = error.response?.data?.details;
                    const userRole = error.response?.data?.userRole;
                    const requiredRoles = error.response?.data?.requiredRoles;

                    let message = 'Access denied: You need administrator privileges to view environment settings.';
                    if (details) {
                        message = details;
                    } else if (userRole && requiredRoles) {
                        message = `Access denied: Your role is '${userRole}' but '${requiredRoles.join(' or ')}' is required.`;
                    }

                    toast.error(message, { duration: 7000 });
                }
            }
        }
    );

    const versionInfo = systemInfo?.version || {};
    const versionNumberDisplay = versionInfo.version && versionInfo.version !== 'unknown' ? versionInfo.version : null;
    const commitDisplay = versionInfo.commit && versionInfo.commit !== 'unknown' ? versionInfo.commit : null;
    const branchDisplay = versionInfo.branch && versionInfo.branch !== 'unknown' ? versionInfo.branch : null;
    const dateDisplay = versionInfo.date && versionInfo.date !== 'unknown' ? new Date(versionInfo.date).toLocaleDateString() : null;
    const nodeVersionDisplay = resolveValue(systemInfo?.nodeVersion);

    const handleBackupDatabase = async () => {
        setBackupLoading(true);
        try {
            // Create a backup download
            const response = await fetch('/api/settings/backup', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error('Backup failed');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `esp8266-platform-backup-${new Date().toISOString().split('T')[0]}.sql`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);

            toast.success(t('settings.backupSuccess', 'Database backup downloaded successfully'));
        } catch (error) {
            console.error('Backup error:', error);
            toast.error(t('settings.backupError', 'Failed to create database backup'));
        } finally {
            setBackupLoading(false);
        }
    };

    // Save settings mutation
    const saveSettingsMutation = useMutation(
        (settings) => apiService.updateSettings(settings),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('settings');
                toast.success(t('settings.saveSuccess', 'Settings saved successfully'));
            },
            onError: (error) => {
                console.error('Save settings error:', error);
                toast.error(t('settings.saveError', 'Failed to save settings'));
            }
        }
    );

    const handleSaveSettings = async () => {
        const allSettings = {
            system: systemSettings,
            branding: {
                ...brandingSettings,
                logoPreview: undefined // Don't send preview data
            }
        };

        // Apply branding changes to the page with hover/focus colors
        if (brandingSettings.primaryColor) {
            applyBrandingColors(brandingSettings.primaryColor);
        }

        // Save to localStorage to persist across page reloads
        localStorage.setItem('appSettings', JSON.stringify(allSettings));

        // Dispatch event to notify other components of settings change
        window.dispatchEvent(new CustomEvent('settingsChanged', { detail: allSettings }));

        saveSettingsMutation.mutate(allSettings);
    };

    const handleLogoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast.error(t('settings.branding.toast.invalidImage', 'Please select a valid image file'));
            return;
        }

        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast.error(t('settings.branding.toast.imageTooLarge', 'Image file size must be less than 2MB'));
            return;
        }

        setLogoUploading(true);

        try {
            // Create preview first
            const reader = new FileReader();
            reader.onload = (e) => {
                setBrandingSettings(prev => ({
                    ...prev,
                    logoPreview: e.target.result
                }));
            };
            reader.readAsDataURL(file);

            // Upload to server
            const formData = new FormData();
            formData.append('logo', file);

            await apiService.uploadLogo(formData);

            // Update branding settings to indicate logo exists
            setBrandingSettings(prev => ({
                ...prev,
                companyLogo: file.name
            }));

            // Refresh settings to get the server logo URL
            queryClient.invalidateQueries('settings');
            toast.success(t('settings.branding.toast.logoUploaded', 'Logo uploaded successfully'));
        } catch (error) {
            console.error('Logo upload error:', error);
            toast.error(t('settings.branding.toast.logoUploadFailed', 'Failed to upload logo'));
            // Reset preview on error
            setBrandingSettings(prev => ({
                ...prev,
                logoPreview: null
            }));
        } finally {
            setLogoUploading(false);
        }
    };

    const removeLogo = async () => {
        try {
            await apiService.removeLogo();
            setBrandingSettings(prev => ({
                ...prev,
                companyLogo: null,
                logoPreview: null
            }));
            queryClient.invalidateQueries('settings');
            toast.success(t('settings.branding.toast.logoRemoved', 'Logo removed'));
        } catch (error) {
            console.error('Remove logo error:', error);
            toast.error(t('settings.branding.toast.logoRemoveFailed', 'Failed to remove logo'));
        }
    };

    // Load SMTP values when email tab is opened
    useEffect(() => {
        const loadSmtpValues = async () => {
            if (activeTab === 'email') {
                try {
                    console.log('Loading SMTP configuration from server...');
                    // First try to get SMTP config from the server (read-only view of process.env)
                    const smtpData = await apiService.getSmtpConfig();
                    console.log('Received SMTP config:', smtpData);

                    if (smtpData?.config) {
                        const newSmtpValues = {
                            SMTP_HOST: smtpData.config.SMTP_HOST || '',
                            SMTP_PORT: smtpData.config.SMTP_PORT || '587',
                            SMTP_SECURE: smtpData.config.SMTP_SECURE || 'false',
                            SMTP_USER: smtpData.config.SMTP_USER || '',
                            SMTP_PASSWORD: '', // Password is never sent from backend for security
                            hasPassword: smtpData.config.hasPassword || false // Track if password is configured on server
                        };
                        console.log('Setting SMTP values from server:', {
                            ...newSmtpValues,
                            SMTP_PASSWORD: newSmtpValues.hasPassword ? '***' : '',
                            hasPassword: newSmtpValues.hasPassword
                        });
                        setSmtpValues(newSmtpValues);
                        setSmtpRequiresRestart(Boolean(smtpData.requiresRestart));
                        return;
                    }
                } catch (error) {
                    console.error('Failed to load SMTP config from server:', error);
                    // If not admin or error, try environment variables endpoint with unmasking
                    try {
                        console.log('Trying to load from environment variables...');
                        const data = await apiService.getEnvironmentVariables(SMTP_VARIABLE_KEYS);
                        console.log('Received environment data:', data);

                        if (data?.variables) {
                            const newSmtpValues = {
                                SMTP_HOST: '',
                                SMTP_PORT: '',
                                SMTP_SECURE: '',
                                SMTP_USER: '',
                                SMTP_PASSWORD: ''
                            };
                            SMTP_VARIABLE_KEYS.forEach(key => {
                                const value = data.variables[key];
                                newSmtpValues[key] = (value && value !== '***MASKED***') ? value : '';
                            });
                            newSmtpValues.hasPassword = !!newSmtpValues.SMTP_PASSWORD;
                            console.log('Setting SMTP values from env:', Object.keys(newSmtpValues).reduce((acc, key) => {
                                acc[key] = key.includes('PASSWORD') ? '***' : newSmtpValues[key];
                                return acc;
                            }, {}));
                            setSmtpValues(newSmtpValues);
                            setSmtpRequiresRestart(false);
                            return;
                        }
                    } catch (envError) {
                        console.error('Failed to load from environment variables:', envError);
                    }
                }

                // Final fallback to locally cached envVars
                console.log('Using fallback from cached envVars');
                const newSmtpValues = {
                    SMTP_HOST: '',
                    SMTP_PORT: '',
                    SMTP_SECURE: '',
                    SMTP_USER: '',
                    SMTP_PASSWORD: ''
                };
                SMTP_VARIABLE_KEYS.forEach(key => {
                    const value = envVars[key];
                    newSmtpValues[key] = (value && value !== '***MASKED***') ? value : '';
                });
                newSmtpValues.hasPassword = !!newSmtpValues.SMTP_PASSWORD;
                setSmtpValues(newSmtpValues);
                setSmtpRequiresRestart(false);
            }
        };

        loadSmtpValues();
    }, [activeTab, envVars]);

    // Environment variable handlers
    const handleEnvVarChange = (key, value) => {
        setEnvVars(prev => ({ ...prev, [key]: value }));

        // Validate on change
        const updatedVars = { ...envVars, [key]: value };
        apiService.validateEnvironmentVariables(updatedVars)
            .then(result => {
                // Handle both result.data and direct result formats
                const validationData = result?.data || result;
                // Ensure we always have the required structure
                setEnvValidation({
                    valid: validationData?.valid ?? true,
                    errors: validationData?.errors || [],
                    warnings: validationData?.warnings || []
                });
            })
            .catch(() => setEnvValidation({ valid: true, errors: [], warnings: [] }));
    };

    const notifyEnvSaveResult = (response, context = 'environment') => {
        const restartStatus = response?.data?.restartStatus;
        const restartRequired = response?.data?.requiresRestart ? 'true' : 'false';
        const baseKey = context === 'email' ? 'settings.email' : 'settings.environment';

        const successKey = `${baseKey}.saveSuccess`;
        const restartNeededKey = `${baseKey}.saveSuccessRestartNeeded`;
        const withRestartKey = `${baseKey}.saveSuccessWithRestart`;
        const restartFailedKey = `${baseKey}.saveSuccessRestartFailed`;

        const fallbacks = {
            default: context === 'email'
                ? 'SMTP settings updated successfully.'
                : 'Environment variables updated successfully.',
            restart: context === 'email'
                ? 'SMTP settings updated successfully. Server restart required.'
                : 'Environment variables updated successfully. Server restart required.',
            withRestart: context === 'email'
                ? 'SMTP settings updated and PM2 restarted successfully!'
                : 'Environment variables updated and PM2 restarted successfully!',
            restartFailed: context === 'email'
                ? 'SMTP settings updated, but PM2 restart failed. Please restart manually.'
                : 'Environment variables updated, but PM2 restart failed. Please restart manually.'
        };

        if (autoRestartPm2) {
            if (restartStatus === 'success') {
                toast.success(t(withRestartKey, fallbacks.withRestart));
                return;
            }

            if (restartStatus === 'failed' || restartStatus === 'error') {
                toast.warning(t(restartFailedKey, fallbacks.restartFailed), { duration: 5000 });
                return;
            }
        }

        const key = restartRequired === 'true' ? restartNeededKey : successKey;
        const fallback = restartRequired === 'true' ? fallbacks.restart : fallbacks.default;
        toast.success(t(key, fallback));
    };

    const handleSaveEnvVars = async () => {
        setSavingEnvVars(true);
        try {
            // Send only environment variables (without autoRestart flag) to the API
            const response = await apiService.updateEnvironmentVariables(envVars, false, autoRestartPm2);

            notifyEnvSaveResult(response, 'environment');

            // If PM2 was restarted, wait a bit before refetching to allow server to fully restart
            if (autoRestartPm2 && response.data?.pm2Restart) {
                setTimeout(() => {
                    refetchEnv();
                    setSavingEnvVars(false);
                }, 2000);
            } else {
                refetchEnv();
                setSavingEnvVars(false);
            }
        } catch (error) {
            console.error('Save environment variables error:', error);
            console.error('Error details:', {
                response: error?.response,
                data: error?.response?.data,
                message: error?.message
            });
            const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Unknown error';
            toast.error(t('settings.environment.saveError', {
                message: errorMessage,
                defaultValue: `Failed to save environment variables: ${errorMessage}`
            }));
            setSavingEnvVars(false);
        }
    };

    const handleSaveSmtpSettings = async () => {
        const smtpPayload = SMTP_VARIABLE_KEYS.reduce((acc, key) => {
            // Use smtpValues instead of envVars
            if (smtpValues[key] !== undefined && smtpValues[key] !== '') {
                acc[key] = smtpValues[key];
            }
            return acc;
        }, {});

        if (Object.keys(smtpPayload).length === 0) {
            toast.error(t('settings.email.noSmtpValues', 'Please fill in the SMTP settings before saving.'));
            return;
        }

        setSavingEmailSettings(true);
        try {
            const response = await apiService.updateEnvironmentVariables(smtpPayload, false, autoRestartPm2);
            notifyEnvSaveResult(response, 'email');

            // If PM2 was restarted, wait a bit before refetching to allow server to fully restart
            if (autoRestartPm2 && response.data?.pm2Restart) {
                setTimeout(() => {
                    refetchEnv();
                }, 2000);
            } else {
                refetchEnv();
            }
        } catch (error) {
            console.error('Save SMTP settings error:', error);
            const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Unknown error';
            toast.error(t('settings.email.saveError', {
                message: errorMessage,
                defaultValue: `Failed to update SMTP settings: ${errorMessage}`
            }));
        } finally {
            setSavingEmailSettings(false);
        }
    };

    const handleTestEmail = async () => {
        const toastId = 'test-email';
        setIsTestingEmail(true);
        try {
            toast.loading(t('settings.email.sendingTestEmail', 'Sending test email...'), { id: toastId });
            const response = await apiService.testEmail();
            toast.success(
                response.data.message || t('settings.email.testEmailSuccess', 'Test email sent successfully!'),
                { id: toastId, duration: 5000 }
            );
        } catch (error) {
            console.error('Failed to send test email:', error);

            // Extract detailed error message
            let errorMessage = t('settings.email.testEmailError', 'Failed to send test email');
            if (error.response?.data?.details) {
                errorMessage = error.response.data.details;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }

            // Show missing fields if available
            if (error.response?.data?.missingFields?.length > 0) {
                errorMessage += `\n\nMissing: ${error.response.data.missingFields.join(', ')}`;
            }

            toast.error(errorMessage, { id: toastId, duration: 7000 });
        } finally {
            setIsTestingEmail(false);
        }
    };

    const categoriesEntries = envData?.categories ? Object.entries(envData.categories) : [];
    const totalEnvVarCount = Object.keys(envVars || {}).length;
    const normalizedEnvSearch = envSearchTerm.trim().toLowerCase();
    const searchActive = normalizedEnvSearch.length > 0;

    const matchesEnvSearch = (...values) => {
        if (!searchActive) return true;
        return values.some(value =>
            (value ?? '').toString().toLowerCase().includes(normalizedEnvSearch)
        );
    };

    const activeCategoryKeys = useMemo(() => {
        if (!envData?.categories) return [];
        return Object.entries(envData.categories)
            .filter(([, variableNames]) => variableNames.some(varName => envVars[varName] !== undefined))
            .map(([category]) => category);
    }, [envData?.categories, envVars]);

    const availableCategoryCount = activeCategoryKeys.length;

    const categorizedVariableKeys = useMemo(() => {
        if (!envData?.categories) return new Set();
        return new Set(Object.values(envData.categories).flat());
    }, [envData?.categories]);

    const otherVariableNames = useMemo(() => {
        return Object.keys(envVars || {}).filter(key => !categorizedVariableKeys.has(key));
    }, [envVars, categorizedVariableKeys]);

    useEffect(() => {
        if (!activeCategoryKeys.length) {
            setCollapsedCategories({});
            return;
        }

        setCollapsedCategories(prev => {
            const next = { ...prev };
            let changed = false;

            activeCategoryKeys.forEach(category => {
                if (next[category] === undefined) {
                    next[category] = false;
                    changed = true;
                }
            });

            Object.keys(next).forEach(category => {
                if (!activeCategoryKeys.includes(category)) {
                    delete next[category];
                    changed = true;
                }
            });

            return changed ? next : prev;
        });
    }, [activeCategoryKeys]);

    const toggleCategoryCollapse = (category) => {
        setCollapsedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const handleExpandCollapseAll = (collapse) => {
        if (!activeCategoryKeys.length) return;
        setCollapsedCategories(prev => {
            const next = { ...prev };
            let changed = false;

            activeCategoryKeys.forEach(category => {
                if ((next[category] ?? false) !== collapse) {
                    next[category] = collapse;
                    changed = true;
                }
            });

            return changed ? next : prev;
        });
    };

    const matchingStats = useMemo(() => {
        if (!searchActive) {
            return {
                variables: totalEnvVarCount,
                categories: availableCategoryCount
            };
        }

        if (!envData?.categories) {
            return { variables: 0, categories: 0 };
        }

        let variablesMatched = 0;
        const matchedCategories = new Set();

        activeCategoryKeys.forEach(category => {
            const variables = envData.categories[category] || [];
            variables.forEach(varName => {
                if (envVars[varName] === undefined) return;
                const apiDescription = envData.descriptions?.[varName] || '';
                const label = t(`settings.environment.variables.${varName}.label`, varName);
                const description = t(`settings.environment.variables.${varName}.description`, apiDescription);
                const value = envVars[varName] ?? '';
                const matches = !searchActive || [varName, label, description, value]
                    .some(entry => (entry ?? '').toString().toLowerCase().includes(normalizedEnvSearch));
                if (matches) {
                    variablesMatched += 1;
                    matchedCategories.add(category);
                }
            });
        });

        return {
            variables: variablesMatched,
            categories: matchedCategories.size
        };
    }, [
        searchActive,
        envData?.categories,
        envData?.descriptions,
        envVars,
        activeCategoryKeys,
        totalEnvVarCount,
        availableCategoryCount,
        normalizedEnvSearch,
        t
    ]);

    const displayedVariableCount = searchActive ? matchingStats.variables : totalEnvVarCount;
    const displayedCategoryCount = searchActive ? matchingStats.categories : availableCategoryCount;
    const filteredOtherVariables = otherVariableNames.filter(varName => matchesEnvSearch(varName, envVars[varName] ?? ''));
    const hasEnvVars = totalEnvVarCount > 0;

    const categoryCards = categoriesEntries.map(([category, variables]) => {
        const categoryVars = variables.filter(varName => envVars[varName] !== undefined);
        if (!categoryVars.length) return null;

        const metadata = ENVIRONMENT_CATEGORY_METADATA[category] || {};
        const CategoryIcon = metadata.icon || SettingsIcon;
        const categoryTitle = t(metadata.titleKey || 'settings.environment.categories.generic.title', metadata.defaultTitle || category);
        const categoryDescription = t(metadata.descriptionKey || 'settings.environment.categories.generic.description', metadata.defaultDescription || '');
        const isCollapsed = collapsedCategories[category] ?? false;

        const enrichedVariables = categoryVars.map(varName => {
            const isSensitive = envData?.sensitiveKeys?.includes(varName);
            const isVisible = showSensitive[varName] || false;
            const apiDescription = envData?.descriptions?.[varName] || '';
            const label = t(`settings.environment.variables.${varName}.label`, varName);
            const description = t(`settings.environment.variables.${varName}.description`, apiDescription);
            const rawValue = envVars[varName] ?? '';
            const hasValue = rawValue && rawValue !== '***MASKED***';
            const isMasked = Boolean(isSensitive && !isVisible && hasValue);
            const matches = matchesEnvSearch(varName, label, description, rawValue);

            return {
                varName,
                label,
                description,
                value: rawValue,
                isSensitive,
                isVisible,
                isMasked,
                matches
            };
        }).filter(variable => variable.matches);

        if (!enrichedVariables.length) return null;

        return (
            <div key={category} className="mb-8 border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex items-start space-x-3">
                        <div className="p-2 bg-white rounded-lg shadow-sm">
                            <CategoryIcon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h4 className="text-lg font-semibold text-gray-900">
                                {categoryTitle}
                            </h4>
                            {categoryDescription && (
                                <p className="text-sm text-gray-600 mt-1">
                                    {categoryDescription}
                                </p>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => toggleCategoryCollapse(category)}
                            className="text-gray-500 hover:text-gray-800 transition-colors"
                            aria-label={isCollapsed
                                ? t('settings.environment.actions.expandCategory', 'Expand category')
                                : t('settings.environment.actions.collapseCategory', 'Collapse category')
                            }
                        >
                            <ChevronDown className={`h-5 w-5 transition-transform ${isCollapsed ? '-rotate-90' : 'rotate-0'}`} />
                        </button>
                    </div>
                </div>

                {!isCollapsed ? (
                    <div className="p-6 bg-white space-y-4">
                        {enrichedVariables.map(({ varName, label, description, isSensitive, isVisible, isMasked, value }) => (
                            <div key={varName} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                <div className="flex flex-col space-y-2">
                                    <div className="flex items-center justify-between">
                                        <label className="text-sm font-semibold text-gray-900">
                                            {label}
                                            {isSensitive && (
                                                <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded font-medium">
                                                    <Shield className="h-3 w-3 inline mr-1" />
                                                    {t('settings.environment.sensitive', 'Sensitive')}
                                                </span>
                                            )}
                                        </label>
                                    </div>

                                    {description && (
                                        <p className="text-xs text-gray-500">
                                            {description}
                                        </p>
                                    )}

                                    <div className="flex items-center space-x-2">
                                        {varName === 'SMTP_SECURE' ? (
                                            <select
                                                value={envVars[varName] || 'false'}
                                                onChange={(e) => handleEnvVarChange(varName, e.target.value)}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                            >
                                                <option value="false">{t('settings.environment.smtp.tls', 'TLS/STARTTLS (Port 587)')}</option>
                                                <option value="true">{t('settings.environment.smtp.ssl', 'SSL (Port 465)')}</option>
                                            </select>
                                        ) : (
                                            <input
                                                type={isSensitive ? (isVisible ? 'text' : 'password') : 'text'}
                                                value={isMasked ? '••••••••••' : (value || '')}
                                                onChange={(e) => handleEnvVarChange(varName, e.target.value)}
                                                placeholder={
                                                    (isSensitive && !isVisible && value)
                                                        ? t('settings.environment.maskPlaceholder', 'Value hidden (click eye to reveal)')
                                                        : description || t('settings.environment.enterValue', {
                                                            name: varName,
                                                            defaultValue: `Enter value for ${varName}`
                                                        })
                                                }
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm bg-white"
                                                disabled={(isSensitive && value === '***MASKED***') || isMasked}
                                                readOnly={(isSensitive && value === '***MASKED***') || isMasked}
                                                autoComplete={isSensitive ? 'off' : 'on'}
                                            />
                                        )}
                                        {isSensitive && (
                                            <button
                                                type="button"
                                                onClick={() => handleSensitiveToggle(varName)}
                                                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                                title={isVisible ? t('settings.environment.hideValue', 'Hide value') : t('settings.environment.showValue', 'Show value')}
                                            >
                                                {isVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </button>
                                        )}
                                    </div>
                                    {isSensitive && isMasked && (
                                        <div className="flex items-start space-x-2 text-xs text-gray-500">
                                            <Shield className="h-3.5 w-3.5 mt-0.5 text-gray-400" />
                                            <p>{t('settings.environment.hiddenValueHelper', 'Hidden by default. Click the eye icon to reveal before editing.')}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="px-6 py-3 text-sm text-gray-500 bg-white border-t border-gray-100">
                        {t('settings.environment.categoryCollapsedNotice', 'Category collapsed. Expand to edit variables.')}
                    </div>
                )}
            </div>
        );
    }).filter(Boolean);

    const hasCategoryCards = categoryCards.length > 0;
    const hasOtherVariables = filteredOtherVariables.length > 0;
    const showNoSearchResults = searchActive && hasEnvVars && !hasCategoryCards && !hasOtherVariables;
    const showNoCategorizedVariables = !searchActive && hasEnvVars && !hasCategoryCards && availableCategoryCount === 0;


    const tabs = [
        { id: 'system', label: t('settings.tabs.system', 'System'), icon: Server },
        { id: 'license', label: t('settings.tabs.license', 'License'), icon: Key },
        { id: 'platform', label: t('settings.tabs.platform', 'Platform Update'), icon: RotateCcw },
        { id: 'branding', label: t('settings.tabs.branding', 'Branding'), icon: Image },
        { id: 'notifications', label: t('settings.tabs.notifications', 'Notification Templates'), icon: MessageSquare },
        { id: 'protocol', label: t('settings.tabs.protocol', 'Protocol Settings'), icon: Plug },
        { id: 'environment', label: t('settings.tabs.environment', 'Environment'), icon: Code },
        { id: 'database', label: t('settings.tabs.database', 'Database'), icon: Database },
        { id: 'email', label: t('settings.tabs.email', 'Email'), icon: Mail },
        { id: 'security', label: t('settings.tabs.security', 'Security'), icon: Shield }
    ];

    return (
        <PageLayout
            title={
                <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-200 via-purple-300 to-pink-300 flex items-center justify-center shadow-lg">
                        <SettingsIcon className="w-6 h-6 text-indigo-900" />
                    </div>
                    <span>{t('settings.title', 'System Settings')}</span>
                </div>
            }
            subtitle={t('settings.subtitle', 'Configure your IoT platform settings and system preferences')}
        >
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Navigation Tabs */}
                <div className="lg:col-span-1">
                    <Card className="sticky top-6">
                        <CardContent className="p-2">
                            <nav className="space-y-0.5">
                                {tabs.map((tab) => {
                                    const Icon = tab.icon;
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${activeTab === tab.id
                                                ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-sm border-l-4 border-blue-600'
                                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-l-4 border-transparent'
                                                }`}
                                        >
                                            <div className={`flex items-center justify-center w-9 h-9 rounded-lg transition-colors ${activeTab === tab.id
                                                ? 'bg-blue-100 text-blue-600'
                                                : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                                                }`}>
                                                <Icon className="h-5 w-5" />
                                            </div>
                                            <span className="flex-1 text-left">{tab.label}</span>
                                        </button>
                                    );
                                })}
                            </nav>
                        </CardContent>
                    </Card>
                </div>

                {/* Content Area */}
                <div className="lg:col-span-3 min-w-0">
                    <Card className="relative z-0">
                        {/* System Tab */}
                        {activeTab === 'system' && (
                            <CardContent className="p-6">
                                <CardTitle className="mb-6">
                                    {t('settings.system.title', 'System Information')}
                                </CardTitle>

                                {/* System Status */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                                            {t('settings.system.status', 'System Status')}
                                        </h4>
                                        <div className="flex items-center">
                                            <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                                            <span className="text-green-700">
                                                {t('settings.system.running', 'Running')}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                                            {t('settings.system.uptime', 'Uptime')}
                                        </h4>
                                        <p className="text-gray-900">
                                            {resolveValue(systemHealth?.uptimeHuman)}
                                        </p>
                                    </div>

                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                            <GitBranch className="w-4 h-4 mr-1" />
                                            {t('settings.system.version', 'Version')}
                                        </h4>
                                        <div className="space-y-1">
                                            <p className="text-gray-900 font-mono text-sm">
                                                {versionNumberDisplay || commitDisplay || t('settings.update.currentVersion.unknown', 'unknown')}
                                            </p>
                                            {versionNumberDisplay && commitDisplay && (
                                                <p className="text-xs text-gray-500">
                                                    {t('settings.update.currentVersion.commitLabel', 'Commit')}: {commitDisplay}
                                                </p>
                                            )}
                                            {branchDisplay && (
                                                <p className="text-xs text-gray-600">
                                                    {t('settings.update.currentVersion.branchLabel', 'Branch')}: {branchDisplay}
                                                </p>
                                            )}
                                            {dateDisplay && (
                                                <p className="text-xs text-gray-500">
                                                    {t('settings.update.currentVersion.dateLabel', 'Build date')}: {dateDisplay}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                                            {t('settings.system.nodeVersion', 'Node.js Version')}
                                        </h4>
                                        <p className="text-gray-900">
                                            {nodeVersionDisplay}
                                        </p>
                                    </div>
                                </div>

                                {/* System Settings Form */}
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('settings.system.adminEmail', 'Admin Email')}
                                        </label>
                                        <input
                                            type="email"
                                            value={systemSettings.adminEmail}
                                            onChange={(e) => setSystemSettings(prev => ({ ...prev, adminEmail: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            {t('settings.system.adminEmailHelp', 'System notifications and alerts will be sent to this email')}
                                        </p>
                                    </div>

                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="maintenanceMode"
                                            checked={systemSettings.maintenanceMode}
                                            onChange={(e) => setSystemSettings(prev => ({ ...prev, maintenanceMode: e.target.checked }))}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label htmlFor="maintenanceMode" className="ml-2 block text-sm text-gray-700">
                                            {t('settings.system.maintenanceMode', 'Maintenance Mode')}
                                        </label>
                                    </div>
                                </div>
                            </CardContent>
                        )}

                        {/* License Tab */}
                        {activeTab === 'license' && (
                            <CardContent className="p-6">
                                <LicenseManagerPanel />
                            </CardContent>
                        )}

                        {/* Platform Update Tab */}
                        {activeTab === 'platform' && (
                            <PlatformUpdateTab />
                        )}

                        {/* Branding Tab */}
                        {activeTab === 'branding' && (
                            <CardContent className="p-6">
                                <CardTitle className="mb-6">
                                    {t('settings.branding.title', 'Brand Customization')}
                                </CardTitle>

                                <div className="space-y-6">
                                    {/* Company Information */}
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-900 mb-4">
                                            {t('settings.branding.companyInfo', 'Company Information')}
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {t('settings.branding.companyName', 'Company Name')}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={brandingSettings.companyName}
                                                    onChange={(e) => setBrandingSettings(prev => ({
                                                        ...prev,
                                                        companyName: e.target.value
                                                    }))}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {t('settings.branding.companyNameHelp', 'Displayed in the header, login screen, and throughout the platform')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Logo Management */}
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-900 mb-4">
                                            {t('settings.branding.logoManagement', 'Logo Management')}
                                        </h4>

                                        <div className="space-y-6">
                                            {/* Company Logo */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {t('settings.branding.companyLogo', 'Company Logo')}
                                                    <span className="text-xs text-gray-500 ml-1">
                                                        (Used in header and login screen)
                                                    </span>
                                                </label>

                                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                                                    {brandingSettings.logoPreview ? (
                                                        <div className="space-y-4">
                                                            <div className="flex justify-center">
                                                                <img
                                                                    src={brandingSettings.logoPreview}
                                                                    alt="Company Logo Preview"
                                                                    className="max-h-24 max-w-full object-contain"
                                                                />
                                                            </div>
                                                            <div className="flex justify-center space-x-2">
                                                                <label
                                                                    className="cursor-pointer text-white px-4 py-2 rounded-md text-sm transition-all"
                                                                    style={{ backgroundColor: 'var(--primary-color)' }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-hover)'}
                                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-color)'}
                                                                >
                                                                    {logoUploading ? 'Uploading...' : 'Change Logo'}
                                                                    <input
                                                                        type="file"
                                                                        accept="image/*"
                                                                        onChange={handleLogoUpload}
                                                                        className="hidden"
                                                                        disabled={logoUploading}
                                                                    />
                                                                </label>
                                                                <button
                                                                    onClick={removeLogo}
                                                                    className="bg-red-600 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700"
                                                                    disabled={logoUploading}
                                                                >
                                                                    <X className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="space-y-4">
                                                            <Image className="w-12 h-12 text-gray-400 mx-auto" />
                                                            <div>
                                                                <label
                                                                    className="cursor-pointer text-white px-4 py-2 rounded-md text-sm transition-all"
                                                                    style={{ backgroundColor: 'var(--primary-color)' }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-hover)'}
                                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-color)'}
                                                                >
                                                                    {logoUploading ? 'Uploading...' : 'Upload Logo'}
                                                                    <input
                                                                        type="file"
                                                                        accept="image/*"
                                                                        onChange={handleLogoUpload}
                                                                        className="hidden"
                                                                        disabled={logoUploading}
                                                                    />
                                                                </label>
                                                                <p className="text-xs text-gray-500 mt-2">
                                                                    {t('settings.branding.logoHint', 'PNG, JPG up to 2MB. Recommended: 200x60px')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Color Scheme */}
                                            <div>
                                                <h4 className="text-sm font-medium text-gray-900 mb-4">
                                                    {t('settings.branding.colorScheme', 'Color Scheme')}
                                                </h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            {t('settings.branding.primaryColor', 'Primary Color')}
                                                        </label>
                                                        <div className="flex items-center space-x-2">
                                                            <input
                                                                type="color"
                                                                value={brandingSettings.primaryColor}
                                                                onChange={(e) => setBrandingSettings(prev => ({
                                                                    ...prev,
                                                                    primaryColor: e.target.value
                                                                }))}
                                                                className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
                                                            />
                                                            <input
                                                                type="text"
                                                                value={brandingSettings.primaryColor}
                                                                onChange={(e) => setBrandingSettings(prev => ({
                                                                    ...prev,
                                                                    primaryColor: e.target.value
                                                                }))}
                                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                                            />
                                                        </div>
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            {t('settings.branding.primaryColorHelp', 'Used for buttons, links, and interactive elements throughout the platform')}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Preview */}
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-900 mb-4">
                                            {t('settings.branding.preview', 'Preview')}
                                        </h4>
                                        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                            <div className="flex items-center space-x-3 mb-4">
                                                {brandingSettings.logoPreview ? (
                                                    <img
                                                        src={brandingSettings.logoPreview}
                                                        alt="Preview"
                                                        className="h-8 object-contain"
                                                    />
                                                ) : (
                                                    <div className="h-8 w-24 bg-gray-300 rounded flex items-center justify-center text-xs">
                                                        {t('settings.branding.logoLabel', 'Logo')}
                                                    </div>
                                                )}
                                                <h5 className="text-lg font-bold">{brandingSettings.companyName}</h5>
                                            </div>
                                            <div className="flex space-x-2">
                                                <div
                                                    className="px-4 py-2 text-white rounded text-sm"
                                                    style={{ backgroundColor: brandingSettings.primaryColor }}
                                                >
                                                    {t('settings.branding.primaryButton', 'Primary Button')}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Save Button */}
                                    <div className="flex justify-end pt-6 border-t">
                                        <button
                                            onClick={handleSaveSettings}
                                            disabled={saveSettingsMutation.isLoading}
                                            className="flex items-center space-x-2 px-6 py-2 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                            style={{
                                                backgroundColor: 'var(--primary-color)',
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!saveSettingsMutation.isLoading) {
                                                    e.currentTarget.style.backgroundColor = 'var(--primary-hover)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--primary-color)';
                                            }}
                                        >
                                            {saveSettingsMutation.isLoading ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    <span>{t('settings.saving', 'Saving...')}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Save className="w-4 h-4" />
                                                    <span>{t('settings.saveSettings', 'Save Settings')}</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </CardContent>
                        )}

                        {/* Environment Variables Tab */}
                        {activeTab === 'environment' && (
                            <CardContent className="p-6 overflow-hidden">
                                <div className="w-full max-w-full overflow-x-hidden">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-medium text-gray-900">
                                            {t('settings.environment.title', 'Environment Variables')}
                                        </h3>
                                        <div className="flex items-center space-x-2">
                                            {envValidation?.errors?.length > 0 && (
                                                <div className="flex items-center space-x-1 text-red-600">
                                                    <AlertTriangle className="h-4 w-4" />
                                                    <span className="text-sm">
                                                        {t('settings.environment.errorCount', {
                                                            count: envValidation.errors.length,
                                                            defaultValue: `${envValidation.errors.length} error(s)`
                                                        })}
                                                    </span>
                                                </div>
                                            )}
                                            {envValidation?.warnings?.length > 0 && (
                                                <div className="flex items-center space-x-1 text-yellow-600">
                                                    <AlertTriangle className="h-4 w-4" />
                                                    <span className="text-sm">
                                                        {t('settings.environment.warningCount', {
                                                            count: envValidation.warnings.length,
                                                            defaultValue: `${envValidation.warnings.length} warning(s)`
                                                        })}
                                                    </span>
                                                </div>
                                            )}
                                    </div>
                                </div>

                                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                                    <div className="flex">
                                        <Shield className="h-5 w-5 text-blue-500" />
                                        <div className="ml-3 text-sm text-blue-900">
                                            <p className="font-medium">
                                                {t('settings.environment.sensitiveInfo.title', 'Sensitive values are hidden')}
                                            </p>
                                            <p className="mt-1">
                                                {t('settings.environment.sensitiveInfo.description', 'Secrets such as tokens and API keys stay hidden until you click the eye icon. Reveal them only when you need to read or edit the value.')}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {envLoading ? (
                                    <div className="animate-pulse space-y-4">
                                        {[1, 2, 3, 4, 5].map(i => (
                                            <div key={i} className="h-16 bg-gray-200 rounded"></div>
                                        ))}
                                        </div>
                                    ) : (
                                        <form onSubmit={(e) => { e.preventDefault(); handleSaveEnvVars(); }}>
                                            {/* No environment variables message */}
                                            {(!envVars || Object.keys(envVars).length === 0) && (
                                                <div className="mb-6 p-6 bg-yellow-50 border border-yellow-200 rounded-md">
                                                    <div className="flex">
                                                        <Info className="h-5 w-5 text-yellow-400" />
                                                        <div className="ml-3">
                                                            <h3 className="text-sm font-medium text-yellow-800">
                                                                {t('settings.environment.emptyTitle', 'No Environment Variables Found')}
                                                            </h3>
                                                            <p className="mt-2 text-sm text-yellow-700">
                                                                {t(
                                                                    'settings.environment.emptyDescription1',
                                                                    "The .env file doesn't exist or is empty. Environment variables are used to configure your application settings like database connections, API keys, and other sensitive configurations."
                                                                )}
                                                            </p>
                                                            <p className="mt-2 text-sm text-yellow-700">
                                                                {t(
                                                                    'settings.environment.emptyDescription2',
                                                                    'You can create environment variables below, and they will be saved to your .env file automatically.'
                                                                )}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Validation Messages */}
                                            {envValidation?.errors?.length > 0 && (
                                                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                                                    <div className="flex">
                                                        <AlertTriangle className="h-5 w-5 text-red-400" />
                                                        <div className="ml-3">
                                                            <h3 className="text-sm font-medium text-red-800">
                                                                {t('settings.environment.errorsTitle', 'Errors')}
                                                            </h3>
                                                            <ul className="mt-2 text-sm text-red-700">
                                                                {(envValidation.errors || []).map((error, idx) => (
                                                                    <li key={idx}>• {error}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {envValidation?.warnings?.length > 0 && (
                                                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                                                    <div className="flex">
                                                        <Info className="h-5 w-5 text-yellow-400" />
                                                        <div className="ml-3">
                                                            <h3 className="text-sm font-medium text-yellow-800">
                                                                {t('settings.environment.warningsTitle', 'Warnings')}
                                                            </h3>
                                                            <ul className="mt-2 text-sm text-yellow-700">
                                                                {(envValidation.warnings || []).map((warning, idx) => (
                                                                    <li key={idx}>• {warning}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Simple form to add environment variables when none exist */}
                                            {(!envVars || Object.keys(envVars).length === 0) && (
                                                <div className="mb-8">
                                                    <h4 className="text-md font-semibold text-gray-800 mb-4 border-b pb-2">
                                                        {t('settings.environment.addTitle', 'Add Environment Variables')}
                                                    </h4>
                                                    <div className="space-y-4">
                                                        <div
                                                            className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center p-4 bg-gray-50 rounded-lg"
                                                            data-env-row="true"
                                                        >
                                                            <div>
                                                                <input
                                                                    type="text"
                                                                    placeholder={t('settings.environment.namePlaceholder', 'Variable Name (e.g., DB_HOST)')}
                                                                    data-env-key-input="true"
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Tab' || e.key === 'Enter') {
                                                                            e.preventDefault();
                                                                            const key = e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '');
                                                                            if (key) {
                                                                                e.target.value = key;
                                                                                const row = e.target.closest('[data-env-row]');
                                                                                const valueInput = row?.querySelector('[data-env-value-input]');
                                                                                valueInput?.focus();
                                                                            }
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="md:col-span-2">
                                                                <div className="flex items-center space-x-2">
                                                                    <input
                                                                        type="text"
                                                                        placeholder={t('settings.environment.valuePlaceholder', 'Value')}
                                                                        data-env-value-input="true"
                                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter') {
                                                                                const row = e.target.closest('[data-env-row]');
                                                                                const keyInput = row?.querySelector('[data-env-key-input]');
                                                                                const key = keyInput?.value.trim();
                                                                                const value = e.target.value.trim();

                                                                                if (key && value) {
                                                                                    handleEnvVarChange(key, value);
                                                                                    if (keyInput) keyInput.value = '';
                                                                                    e.target.value = '';
                                                                                    keyInput?.focus();
                                                                                }
                                                                            }
                                                                        }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            const row = e.target.closest('[data-env-row]');
                                                                            const keyInput = row?.querySelector('[data-env-key-input]');
                                                                            const valueInput = row?.querySelector('[data-env-value-input]');
                                                                            const key = keyInput?.value.trim();
                                                                            const value = valueInput?.value.trim();

                                                                            if (key && value) {
                                                                                handleEnvVarChange(key, value);
                                                                                if (keyInput) keyInput.value = '';
                                                                                if (valueInput) valueInput.value = '';
                                                                                keyInput?.focus();
                                                                            }
                                                                        }}
                                                                        className="px-3 py-2 text-white rounded-md text-sm transition-all"
                                                                        style={{ backgroundColor: 'var(--primary-color)' }}
                                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-hover)'}
                                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-color)'}
                                                                    >
                                                                        {t('settings.environment.addButton', 'Add')}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <p className="text-xs text-gray-500">
                                                            {t(
                                                                'settings.environment.hint',
                                                                'Press Tab to format variable names, or Enter to add the variable'
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}

                                            {hasEnvVars && (
                                                <>
                                                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
                                                        <div className="relative w-full">
                                                            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                                            <input
                                                                type="text"
                                                                value={envSearchTerm}
                                                                onChange={(e) => setEnvSearchTerm(e.target.value)}
                                                                placeholder={t('settings.environment.searchPlaceholder', 'Search variables by name or description')}
                                                                className="w-full pl-9 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                                aria-label={t('settings.environment.searchPlaceholder', 'Search variables by name or description')}
                                                            />
                                                            {envSearchTerm && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setEnvSearchTerm('')}
                                                                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                                                                    aria-label={t('settings.environment.actions.clearSearch', 'Clear filter')}
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleExpandCollapseAll(false)}
                                                                disabled={!hasCategoryCards}
                                                                className="flex items-center space-x-1"
                                                            >
                                                                <ChevronDown className="h-4 w-4" />
                                                                <span>{t('settings.environment.actions.expandAll', 'Expand all')}</span>
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleExpandCollapseAll(true)}
                                                                disabled={!hasCategoryCards}
                                                                className="flex items-center space-x-1"
                                                            >
                                                                <ChevronDown className="h-4 w-4 -rotate-90" />
                                                                <span>{t('settings.environment.actions.collapseAll', 'Collapse all')}</span>
                                                            </Button>
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-wrap gap-4 mb-8">
                                                        <div className="flex-1 min-w-[160px] px-4 py-3 bg-white border border-gray-200 rounded-lg shadow-sm">
                                                            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                                                                {t('settings.environment.summary.totalVariables', 'Total variables')}
                                                            </p>
                                                            <p className="text-2xl font-semibold text-gray-900">{totalEnvVarCount}</p>
                                                        </div>
                                                        <div className="flex-1 min-w-[160px] px-4 py-3 bg-white border border-gray-200 rounded-lg shadow-sm">
                                                            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                                                                {t('settings.environment.summary.totalCategories', 'Active categories')}
                                                            </p>
                                                            <p className="text-2xl font-semibold text-gray-900">{availableCategoryCount}</p>
                                                        </div>
                                                        {searchActive && (
                                                            <>
                                                                <div className="flex-1 min-w-[160px] px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
                                                                    <p className="text-xs uppercase tracking-wide text-blue-700 mb-1">
                                                                        {t('settings.environment.summary.matchingVariables', 'Matching variables')}
                                                                    </p>
                                                                    <p className="text-2xl font-semibold text-blue-900">{displayedVariableCount}</p>
                                                                </div>
                                                                <div className="flex-1 min-w-[160px] px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
                                                                    <p className="text-xs uppercase tracking-wide text-blue-700 mb-1">
                                                                        {t('settings.environment.summary.matchingCategories', 'Matching categories')}
                                                                    </p>
                                                                    <p className="text-2xl font-semibold text-blue-900">{displayedCategoryCount}</p>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </>
                                            )}

                                            {/* Environment Variables by Category */}
                                            {hasCategoryCards ? categoryCards : null}

                                            {showNoCategorizedVariables && (
                                                <div className="mb-8 p-6 text-center border border-dashed border-gray-200 rounded-lg bg-white">
                                                    <p className="text-sm text-gray-600">
                                                        {t('settings.environment.noCategorizedVariables', 'No categorized environment variables available yet.')}
                                                    </p>
                                                </div>
                                            )}

                                            {showNoSearchResults && (
                                                <div className="mb-8 p-6 text-center border border-dashed border-gray-300 rounded-lg bg-gray-50">
                                                    <p className="text-sm font-semibold text-gray-800">
                                                        {t('settings.environment.noResultsTitle', 'No variables match your search')}
                                                    </p>
                                                    <p className="text-sm text-gray-600 mt-1">
                                                        {t('settings.environment.noResultsDescription', 'Try a different keyword or clear the filter.')}
                                                    </p>
                                                </div>
                                            )}

                                            {hasOtherVariables && (
                                                <div className="mb-8 border border-gray-200 rounded-lg overflow-hidden">
                                                    <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-6 py-4 border-b border-gray-200">
                                                        <div className="flex items-start space-x-3">
                                                            <div className="p-2 bg-white rounded-lg shadow-sm">
                                                                <SettingsIcon className="h-5 w-5" />
                                                            </div>
                                                            <div className="flex-1">
                                                                <h4 className="text-lg font-semibold text-gray-900">
                                                                    {t('settings.environment.otherVariables', 'Other Variables')}
                                                                </h4>
                                                                <p className="text-sm text-gray-600 mt-1">
                                                                    {t('settings.environment.customDescription', 'Custom environment variables not in predefined categories')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="p-6 bg-white space-y-4">
                                                        {filteredOtherVariables.map(varName => (
                                                            <div key={varName} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                                                <div className="flex flex-col space-y-2">
                                                                    <label className="text-sm font-semibold text-gray-900">
                                                                        {varName}
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        value={envVars[varName] || ''}
                                                                        onChange={(e) => handleEnvVarChange(varName, e.target.value)}
                                                                        placeholder={t('settings.environment.enterValue', {
                                                                            name: varName,
                                                                            defaultValue: `Enter ${varName}`
                                                                        })}
                                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm bg-white"
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                        </form>
                                    )}

                                    <div className="mt-6 border-t border-gray-200 pt-6">
                                        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                                            <div className="flex items-start space-x-2 text-sm text-gray-600">
                                                <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5" />
                                                <div>
                                                    <p>{t('settings.environment.restartNotice', 'Changes may require server restart to take effect')}</p>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {t('settings.environment.saveNote', 'All environment categories are saved together. Review changes before saving.')}
                                                    </p>
                                                </div>
                                            </div>
                                            <label className="flex items-center text-sm text-gray-700 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={autoRestartPm2}
                                                    onChange={(e) => setAutoRestartPm2(e.target.checked)}
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                                                />
                                                <RefreshCw className="h-4 w-4 mr-1" />
                                                {t('settings.environment.autoRestartPm2', 'Automatically restart PM2 after saving')}
                                            </label>
                                        </div>
                                        <div className="flex justify-end mt-4">
                                            <button
                                                type="button"
                                                onClick={handleSaveEnvVars}
                                                disabled={savingEnvVars || envValidation?.errors?.length > 0}
                                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all"
                                                style={{
                                                    backgroundColor: savingEnvVars || envValidation?.errors?.length > 0 ? undefined : 'var(--primary-color)',
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!savingEnvVars && !envValidation?.errors?.length) {
                                                        e.currentTarget.style.backgroundColor = 'var(--primary-hover)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!savingEnvVars && !envValidation?.errors?.length) {
                                                        e.currentTarget.style.backgroundColor = 'var(--primary-color)';
                                                    }
                                                }}
                                                onFocus={(e) => {
                                                    e.currentTarget.style.setProperty('--tw-ring-color', 'var(--primary-color)');
                                                }}
                                            >
                                                {savingEnvVars ? (
                                                    <>
                                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                        {t('settings.saving', 'Saving...')}
                                                    </>
                                                ) : (
                                                    <>
                                                        <Save className="h-4 w-4 mr-2" />
                                                        {t('settings.environment.save', 'Save Environment Variables')}
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        )}

                        {/* Database Tab */}
                        {activeTab === 'database' && (
                            <div className="p-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    {t('settings.database.title', 'Database Management')}
                                </h3>

                                <div className="space-y-6">
                                    {/* Database Info */}
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                        <div className="flex items-start">
                                            <Info className="h-5 w-5 text-blue-400 mt-0.5" />
                                            <div className="ml-3">
                                                <h4 className="text-sm font-medium text-blue-800">
                                                    {t('settings.database.info', 'Database Information')}
                                                </h4>
                                                <div className="mt-2 text-sm text-blue-700">
                                                    <p>{t('settings.database.type', 'Type')}: PostgreSQL</p>
                                                    <p>{t('settings.database.status', 'Status')}: Connected</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Backup Section */}
                                    <div>
                                        <h4 className="text-md font-medium text-gray-900 mb-3">
                                            {t('settings.database.backup', 'Backup & Restore')}
                                        </h4>

                                        <div className="space-y-3">
                                            <button
                                                onClick={handleBackupDatabase}
                                                disabled={backupLoading}
                                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                                style={{ backgroundColor: 'var(--primary-color)' }}
                                                onMouseEnter={(e) => {
                                                    if (!backupLoading) {
                                                        e.currentTarget.style.backgroundColor = 'var(--primary-hover)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'var(--primary-color)';
                                                }}
                                                onFocus={(e) => {
                                                    e.currentTarget.style.setProperty('--tw-ring-color', 'var(--primary-color)');
                                                }}
                                            >
                                                {backupLoading ? (
                                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                                ) : (
                                                    <Download className="h-4 w-4 mr-2" />
                                                )}
                                                {t('settings.database.createBackup', 'Create Backup')}
                                            </button>

                                            <p className="text-sm text-gray-600">
                                                {t('settings.database.backupDescription', 'Download a complete backup of your database including all devices, users, alerts, and telemetry data.')}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Retention Settings */}
                                    <div>
                                        <h4 className="text-md font-medium text-gray-900 mb-3">
                                            {t('settings.database.retention', 'Data Retention')}
                                        </h4>

                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    {t('settings.database.backupRetention', 'Backup Retention (days)')}
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="365"
                                                    value={systemSettings.backupRetentionDays}
                                                    onChange={(e) => setSystemSettings(prev => ({ ...prev, backupRetentionDays: parseInt(e.target.value) }))}
                                                    className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    {t('settings.database.logRetention', 'Log Retention (days)')}
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="90"
                                                    value={systemSettings.logRetentionDays}
                                                    onChange={(e) => setSystemSettings(prev => ({ ...prev, logRetentionDays: parseInt(e.target.value) }))}
                                                    className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Email Tab */}
                        {activeTab === 'email' && (
                            <div className="p-6 space-y-6">
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900">
                                        {t('settings.email.title', 'Email Configuration')}
                                    </h3>
                                    <p className="mt-1 text-sm text-gray-500">
                                        {t('settings.email.description', 'Test your email configuration and verify SMTP settings are working correctly.')}
                                    </p>
                                </div>

                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <div className="flex items-start">
                                        <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                                        <div className="ml-3">
                                            <p className="text-sm text-blue-900 font-medium">
                                                {t('settings.email.configureNotice', 'Configure Email Settings')}
                                            </p>
                                            <p className="text-sm text-blue-800 mt-1">
                                                {t('settings.email.environmentTabNotice', 'Email settings (SMTP) are configured in the Environment tab. Go to')}
                                                {' '}
                                                <button
                                                    onClick={() => setActiveTab('environment')}
                                                    className="font-semibold underline hover:text-blue-900"
                                                >
                                                    {t('settings.tabs.environment', 'Environment')}
                                                </button>
                                                {' '}
                                                {t('settings.email.toConfigureSMTP', 'to configure SMTP settings.')}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Current SMTP Configuration (Read-Only) */}
                                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                        <h4 className="text-md font-semibold text-gray-900">
                                            {t('settings.email.currentConfig', 'Current SMTP Configuration')}
                                        </h4>
                                        <p className="text-sm text-gray-600 mt-1">
                                            {t('settings.email.readOnlyConfig', 'Read-only view of current server configuration')}
                                        </p>
                                    </div>
                                    {smtpRequiresRestart && (
                                        <div className="px-6 py-4 bg-yellow-50 border-b border-yellow-200 text-sm text-yellow-800">
                                            <p className="font-medium">
                                                {t('settings.email.restartRequired', 'Pending SMTP changes detected')}
                                            </p>
                                            <p className="mt-1">
                                                {t('settings.email.restartRequiredDescription', 'The .env file contains SMTP settings that are not yet active. Restart the backend service (or use auto-restart) to apply them.')}
                                            </p>
                                        </div>
                                    )}
                                    <div className="px-6 py-4 space-y-3">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <span className="text-sm font-medium text-gray-700">{t('settings.email.smtpHost', 'SMTP Host')}:</span>
                                                <p className="text-sm text-gray-900 mt-1 font-mono bg-gray-50 px-2 py-1 rounded">
                                                    {smtpValues.SMTP_HOST || <span className="text-gray-400">{t('common.notConfigured', 'Not configured')}</span>}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-sm font-medium text-gray-700">{t('settings.email.smtpPort', 'SMTP Port')}:</span>
                                                <p className="text-sm text-gray-900 mt-1 font-mono bg-gray-50 px-2 py-1 rounded">
                                                    {smtpValues.SMTP_PORT || <span className="text-gray-400">{t('common.notConfigured', 'Not configured')}</span>}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-sm font-medium text-gray-700">{t('settings.email.username', 'Username')}:</span>
                                                <p className="text-sm text-gray-900 mt-1 font-mono bg-gray-50 px-2 py-1 rounded">
                                                    {smtpValues.SMTP_USER || <span className="text-gray-400">{t('common.notConfigured', 'Not configured')}</span>}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-sm font-medium text-gray-700">{t('settings.email.smtpSecure', 'Encryption')}:</span>
                                                <p className="text-sm text-gray-900 mt-1 font-mono bg-gray-50 px-2 py-1 rounded">
                                                    {smtpValues.SMTP_SECURE === 'true' ? 'SSL/TLS (Port 465)' :
                                                        smtpValues.SMTP_SECURE === 'none' ? 'None (Port 25)' :
                                                            'STARTTLS (Port 587)'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="pt-2 border-t border-gray-100">
                                            <span className="text-sm font-medium text-gray-700">{t('settings.email.password', 'Password')}:</span>
                                            <p className="text-sm text-gray-500 mt-1 italic">
                                                {smtpValues.hasPassword ?
                                                    t('settings.email.passwordConfigured', 'Configured (hidden for security)') :
                                                    t('common.notConfigured', 'Not configured')
                                                }
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Test Email Section */}
                                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                        <h4 className="text-md font-semibold text-gray-900">
                                            {t('settings.email.testEmail', 'Test Email Configuration')}
                                        </h4>
                                        <p className="text-sm text-gray-600 mt-1">
                                            {t('settings.email.testEmailDescription', 'Send a test email to verify your SMTP configuration is working correctly.')}
                                        </p>
                                    </div>
                                    <div className="px-6 py-4">
                                        <Button
                                            variant="default"
                                            onClick={handleTestEmail}
                                            disabled={isTestingEmail || !smtpValues.SMTP_HOST}
                                        >
                                            {isTestingEmail ? (
                                                <>
                                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                                    {t('settings.email.sendingTest', 'Sending...')}
                                                </>
                                            ) : (
                                                <>
                                                    <Mail className="h-4 w-4 mr-2" />
                                                    {t('settings.email.sendTest', 'Send Test Email')}
                                                </>
                                            )}
                                        </Button>
                                        {!smtpValues.SMTP_HOST && (
                                            <p className="text-sm text-amber-600 mt-2 flex items-center">
                                                <AlertTriangle className="h-4 w-4 mr-1" />
                                                {t('settings.email.configureFirst', 'Please configure SMTP settings in the Environment tab first')}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Security Tab */}
                        {activeTab === 'security' && (
                            <CardContent className="p-6">
                                <CardTitle className="mb-6">
                                    {t('settings.security.title', 'Security Settings')}
                                </CardTitle>

                                <div className="space-y-6">
                                    <div>
                                        <h4 className="text-md font-medium text-gray-900 mb-3">
                                            {t('settings.security.authentication', 'Authentication')}
                                        </h4>
                                        <p className="text-sm text-gray-600 mb-3">
                                            {t('settings.security.authDescription', 'Configure authentication and session settings')}
                                        </p>

                                        <div className="space-y-3">
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id="forceHttps"
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                />
                                                <label htmlFor="forceHttps" className="ml-2 block text-sm text-gray-700">
                                                    {t('settings.security.forceHttps', 'Force HTTPS')}
                                                </label>
                                            </div>

                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id="requireStrongPasswords"
                                                    defaultChecked
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                />
                                                <label htmlFor="requireStrongPasswords" className="ml-2 block text-sm text-gray-700">
                                                    {t('settings.security.strongPasswords', 'Require Strong Passwords')}
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <h4 className="text-md font-medium text-gray-900 mb-3">
                                            {t('settings.security.sessions', 'Session Management')}
                                        </h4>

                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    {t('settings.security.sessionTimeout', 'Session Timeout (hours)')}
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="168"
                                                    defaultValue="24"
                                                    className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        )}

                        {/* Notification Templates Tab */}
                        {activeTab === 'notifications' && (
                            <CardContent className="p-6">
                                <NotificationTemplates />
                            </CardContent>
                        )}

                        {/* Protocol Settings Tab */}
                        {activeTab === 'protocol' && (
                            <CardContent className="p-6">
                                <ProtocolSettingsManager />
                            </CardContent>
                        )}

                        {/* Save Button */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
                            <Button
                                onClick={handleSaveSettings}
                                className="inline-flex items-center"
                            >
                                <Save className="h-4 w-4 mr-2" />
                                {t('settings.save', 'Save Settings')}
                            </Button>
                        </div>
                    </Card>
                </div>
            </div>
        </PageLayout>
    );
}

// Platform Update Tab Component
function PlatformUpdateTab() {
    const { t } = useTranslation();
    const [updating, setUpdating] = useState(false);
    const [showLogs, setShowLogs] = useState(false);

    const updateCopy = useMemo(() => {
        const result = t('settings.update', { returnObjects: true });
        return typeof result === 'string' ? {} : result;
    }, [t]);

    const getUpdate = (path, fallback) => {
        const value = path.split('.').reduce((acc, key) => {
            if (acc && typeof acc === 'object' && key in acc) {
                return acc[key];
            }
            return undefined;
        }, updateCopy);
        return value ?? fallback;
    };

    // Query system version - NO auto-refresh
    const { data: versionData, isLoading: versionLoading, refetch: refetchVersion } = useQuery(
        'system-version',
        apiService.getSystemVersion,
        {
            refetchOnWindowFocus: false, // Disable auto-refresh
            onError: (error) => {
                console.error('Failed to load version info:', error);
            }
        }
    );

    // Query update status
    const { data: updateStatus, isLoading: updateLoading } = useQuery(
        'update-status',
        apiService.getUpdateStatus,
        {
            refetchOnWindowFocus: false, // Disable auto-refresh
            staleTime: 2 * 60 * 1000, // Consider data fresh for 2 minutes
            onError: (error) => {
                console.error('Failed to check update status:', error);
            }
        }
    );

    // Query update progress (only when updating)
    const { data: progressData, isLoading: progressLoading } = useQuery(
        'update-progress',
        apiService.getUpdateProgress,
        {
            enabled: updating,
            refetchInterval: 1000, // Poll every second when updating
            refetchOnWindowFocus: false, // Don't refetch on focus
            onSuccess: (data) => {
                console.log('Update progress data:', data);
                const status = data?.status;
                if (status && !status.isRunning && updating) {
                    setUpdating(false);
                    if (status.error) {
                        toast.error(`${getUpdate('messages.failed', 'Update failed')}: ${status.error}`);
                    } else {
                        toast.success(getUpdate('messages.complete', 'Platform update completed successfully!'));
                        refetchVersion();
                    }
                }
            },
            onError: (error) => {
                console.error('Failed to get update progress:', error);
            }
        }
    );

    // Update platform mutation
    const updatePlatformMutation = useMutation(
        apiService.updatePlatform,
        {
            onSuccess: () => {
                toast.success(getUpdate('messages.started', 'Platform update started! Monitor progress below.'));
                setUpdating(true);
            },
            onError: (error) => {
                const message = error.response?.data?.error || getUpdate('messages.startError', 'Failed to start platform update');
                toast.error(message);
                setUpdating(false);
            }
        }
    );

    const handleUpdate = async () => {
        if (!updateStatus?.updateScript) {
            toast.error(getUpdate('messages.scriptMissing', 'Update script is not available on this system'));
            return;
        }

        const confirmed = window.confirm(
            getUpdate('messages.confirm', 'Are you sure you want to update the platform? This will restart the system and may take several minutes. Make sure no critical operations are running.')
        );

        if (confirmed) {
            setUpdating(true);
            updatePlatformMutation.mutate();
        }
    };

    const version = versionData?.version || {};

    return (
        <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2 flex items-center">
                <RotateCcw className="w-5 h-5 mr-2 text-blue-600" />
                {t('settings.update.title', 'Platform Update')}
            </h3>

            {/* Information Banner */}
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                    <Info className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-900">
                        <p className="font-medium mb-1">
                            {t('settings.update.autoUpdateInfo.title', 'Automatic Updates via GitHub Actions')}
                        </p>
                        <p className="text-blue-800">
                            {t('settings.update.autoUpdateInfo.description',
                                'The platform is automatically updated through GitHub Actions workflows whenever changes are pushed to the repository. Manual updates are also available below when new versions are detected.'
                            )}
                        </p>
                    </div>
                </div>
            </div>

            <div className="space-y-6">
                {/* Current Version Info */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                        <GitBranch className="w-4 h-4 mr-2" />
                        {t('settings.update.currentVersion.title', 'Current Version')}
                    </h4>

                    {versionLoading ? (
                        <div className="animate-pulse">
                            <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div className="flex justify-between">
                                <span className="font-medium text-gray-700">{t('settings.update.currentVersion.commit', 'Commit')}:</span>
                                <span className="font-mono text-gray-900">
                                    {version.commit || t('settings.update.currentVersion.unknown', 'unknown')}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium text-gray-700">{t('settings.update.currentVersion.branch', 'Branch')}:</span>
                                <span className="text-gray-900">
                                    {version.branch || t('settings.update.currentVersion.unknown', 'unknown')}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium text-gray-700">{t('settings.update.currentVersion.author', 'Author')}:</span>
                                <span className="text-gray-900">
                                    {version.author || t('settings.update.currentVersion.unknown', 'unknown')}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium text-gray-700">{t('settings.update.currentVersion.date', 'Date')}:</span>
                                <span className="text-gray-900 flex items-center">
                                    <Clock className="w-3 h-3 mr-1" />
                                    {version.date ? new Date(version.date).toLocaleDateString() : t('settings.update.currentVersion.unknown', 'unknown')}
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <button
                            onClick={() => refetchVersion()}
                            disabled={versionLoading}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <RefreshCw className={`w-4 h-4 mr-2 ${versionLoading ? 'animate-spin' : ''}`} />
                            {t('settings.update.currentVersion.refresh', 'Refresh Version Info')}
                        </button>
                    </div>
                </div>

                {/* Update Section */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                        <RotateCcw className="w-4 h-4 mr-2" />
                        {t('settings.update.availability.title', 'Platform Update')}
                    </h4>

                    {updateLoading ? (
                        <div className="animate-pulse">
                            <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
                            <div className="h-10 bg-gray-200 rounded w-32"></div>
                        </div>
                    ) : updateStatus?.updateAvailable ? (
                        <div>
                            <div className="mb-4">
                                <div className="flex items-center text-green-600 mb-2">
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    <span className="text-sm font-medium">
                                        {t('settings.update.availability.available', 'Update available')}
                                    </span>
                                </div>
                                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                                    <p className="text-sm text-blue-900">
                                        {t('settings.update.availability.behindBy', {
                                            count: updateStatus.behindBy,
                                            defaultValue:
                                                updateStatus.behindBy === 1
                                                    ? '1 new commit available'
                                                    : `${updateStatus.behindBy} new commits available`
                                        })}
                                    </p>
                                    <p className="text-xs text-blue-700 mt-1">
                                        {t('settings.update.availability.currentLabel', 'Current')}:{' '}
                                        <code className="bg-blue-100 px-1 py-0.5 rounded">{updateStatus.currentCommit}</code>
                                        {' → '}
                                        {t('settings.update.availability.remoteLabel', 'Remote')}:{' '}
                                        <code className="bg-blue-100 px-1 py-0.5 rounded">{updateStatus.remoteCommit}</code>
                                    </p>
                                </div>
                                <p className="text-sm text-gray-600 mb-4">
                                    {t('settings.update.availability.scriptLabel', 'Script')}:{' '}
                                    <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">{updateStatus.updateScript}</code>
                                </p>
                                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                                    <div className="flex">
                                        <AlertTriangle className="w-5 h-5 text-yellow-400 mr-2" />
                                        <div className="text-sm">
                                            <p className="text-yellow-800 font-medium">
                                                {t('settings.update.availability.important', 'Important:')}
                                            </p>
                                            <ul className="text-yellow-700 mt-2 list-disc list-inside space-y-1">
                                                <li>{t('settings.update.availability.warning1', 'The update process will restart the entire system')}</li>
                                                <li>{t('settings.update.availability.warning2', 'All users will be disconnected temporarily')}</li>
                                                <li>{t('settings.update.availability.warning3', 'The process may take several minutes to complete')}</li>
                                                <li>{t('settings.update.availability.warning4', 'Make sure no critical operations are running')}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleUpdate}
                                disabled={updating}
                                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${updating
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                                    }`}
                            >
                                {updating ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                        {t('settings.update.availability.updating', 'Updating...')}
                                    </>
                                ) : (
                                    <>
                                        <RotateCcw className="w-4 h-4 mr-2" />
                                        {t('settings.update.availability.updateButton', 'Update Platform')}
                                    </>
                                )}
                            </button>
                        </div>
                    ) : (
                        <div>
                            <div className="flex items-center text-green-600 mb-2">
                                <CheckCircle className="w-4 h-4 mr-2" />
                                <span className="text-sm font-medium">
                                    {t('settings.update.upToDate.title', 'System is up to date')}
                                </span>
                            </div>
                            {updateStatus?.currentCommit && updateStatus?.remoteCommit && (
                                <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
                                    <p className="text-sm text-green-900">
                                        {t('settings.update.upToDate.runningLatest', 'Running the latest version')}
                                    </p>
                                    <p className="text-xs text-green-700 mt-1">
                                        {t('settings.update.upToDate.commitLabel', 'Commit')}:{' '}
                                        <code className="bg-green-100 px-1 py-0.5 rounded">{updateStatus.currentCommit}</code>
                                    </p>
                                </div>
                            )}
                            <p className="text-sm text-gray-600 mb-4">
                                {t('settings.update.upToDate.synchronized', 'Your platform is synchronized with the remote repository.')}
                            </p>
                            <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                                <div className="text-sm">
                                    <p className="text-gray-800 font-medium">
                                        {t('settings.update.upToDate.instructionsTitle', 'To enable platform updates:')}
                                    </p>
                                    <ul className="text-gray-600 mt-2 list-disc list-inside space-y-1">
                                        <li>
                                            {t('settings.update.upToDate.instruction1', 'Create an')}{' '}
                                            <code className="bg-white px-1 py-0.5 rounded text-xs">
                                                {t('settings.update.commands.updateSystem', 'update-system')}
                                            </code>{' '}
                                            {t('settings.update.upToDate.instruction1Suffix', 'script')}
                                        </li>
                                        <li>{t('settings.update.upToDate.instruction2', 'Make it executable and place it in your system PATH')}</li>
                                        <li>
                                            {t('settings.update.upToDate.instruction3Prefix', 'Or create')}{' '}
                                            <code className="bg-white px-1 py-0.5 rounded text-xs">
                                                {t('settings.update.commands.updateSystemScript', 'update-system.sh')}
                                            </code>{' '}
                                            {t('settings.update.upToDate.instruction3Suffix', 'in the project directory')}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Progress Tracking Section - Only show when updating */}
                {updating && progressData?.status && (
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                            <Clock className="w-4 h-4 mr-2" />
                            {t('settings.update.progress.title', 'Update Progress')}
                        </h4>

                        <div className="space-y-4">
                            {/* Progress Bar */}
                            <div>
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-gray-600">{t('settings.update.progress.progressLabel', 'Progress')}</span>
                                    <span className="font-medium text-gray-900">
                                        {progressData.status.progress}%
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                                        style={{ width: `${progressData.status.progress}%` }}
                                    ></div>
                                </div>
                            </div>

                            {/* Current Step */}
                            <div className="flex items-center text-sm">
                                <span className="text-gray-600 mr-2">
                                    {t('settings.update.progress.currentStep', 'Current step:')}
                                </span>
                                <span className="font-medium text-gray-900 flex items-center">
                                    {progressData.status.isRunning && (
                                        <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                                    )}
                                    {progressData.status.currentStep || t('settings.update.progress.initializing', 'Initializing...')}
                                </span>
                            </div>

                            {/* Duration */}
                            {progressData.status.duration && (
                                <div className="flex items-center text-sm text-gray-600">
                                    <span className="mr-2">{t('settings.update.progress.duration', 'Duration:')}</span>
                                    <span>{Math.floor(progressData.status.duration / 1000)}s</span>
                                </div>
                            )}

                            {/* Error Message */}
                            {progressData.status.error && (
                                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                                    <div className="flex items-start">
                                        <X className="w-4 h-4 text-red-400 mt-0.5 mr-2 flex-shrink-0" />
                                        <div>
                                            <h5 className="text-sm font-medium text-red-800">
                                                {t('settings.update.progress.failed', 'Update Failed')}
                                            </h5>
                                            <p className="mt-1 text-sm text-red-700">{progressData.status.error}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Logs Section */}
                            {progressData.status.logs && progressData.status.logs.length > 0 && (
                                <div className="mt-6">
                                    <div className="flex items-center justify-between mb-3">
                                        <h5 className="text-sm font-medium text-gray-900">
                                            {t('settings.update.progress.logsTitle', 'Update Logs')}
                                        </h5>
                                        <button
                                            onClick={() => setShowLogs(!showLogs)}
                                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                                        >
                                            {showLogs ? (
                                                <>
                                                    <EyeOff className="w-3 h-3 mr-1" />
                                                    {t('settings.update.progress.hideLogs', 'Hide Logs')}
                                                </>
                                            ) : (
                                                <>
                                                    <Eye className="w-3 h-3 mr-1" />
                                                    {t('settings.update.progress.showLogs', 'Show Logs ({{count}})', {
                                                        count: progressData.status.logs.length
                                                    })}
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {showLogs && (
                                        <div className="bg-gray-900 text-gray-100 rounded-md p-4 font-mono text-xs overflow-auto max-h-60">
                                            {progressData.status.logs.slice(-20).map((log, index) => (
                                                <div
                                                    key={index}
                                                    className={`mb-1 ${log.type === 'error' ? 'text-red-300' : 'text-gray-300'
                                                        }`}
                                                >
                                                    <span className="text-gray-500 mr-2">
                                                        {new Date(log.timestamp).toLocaleTimeString()}
                                                    </span>
                                                    {log.message}
                                                </div>
                                            ))}
                                            {progressData.status.logs.length > 20 && (
                                                <div className="text-gray-500 text-center mt-2">
                                                    {t('settings.update.progress.logSummary', '... showing last 20 entries of {{total}} total', {
                                                        total: progressData.status.logs.length
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Settings;
